/**
 * NumberRoller — the "clunk count-up" from §5 of the masterplan.
 *
 * Takes a Phaser text object and animates its displayed value from a start
 * number to a target number with juice:
 *   • Count-up with easing (not linear)
 *   • Pulse scale on each tick (1.0 → 1.06 → 1.0)
 *   • Tick SFX per increment (throttled)
 *   • Bigger "thunk" SFX on final settle
 *   • Gold particle burst on milestone multiples (every `milestone` increment)
 *   • Optional floating "+N" sprite drifting up and fading
 *
 * Usage:
 *   const roller = new NumberRoller(scene, scoreText);
 *   roller.rollTo(currentScore, newScore);
 */

import Phaser from 'phaser';
import { AudioManager } from './AudioManager';
import { COLORS_HEX, MOTION, FONTS } from '../ui/theme';

export interface NumberRollerOptions {
  /** Duration of the entire roll in ms. Default 800. */
  duration?: number;
  /** Easing function. Default Back.easeOut for the clunk. */
  ease?: string;
  /** Format the number (e.g. thousands separator). Default comma-separated. */
  formatter?: (n: number) => string;
  /** Play ticking SFX during roll. Default true. */
  playTicks?: boolean;
  /** SFX key for individual tick. Default sfx_score_tick. */
  tickSfxKey?: string;
  /** SFX key for final settle. Default sfx_combo (works as a chime). */
  settleSfxKey?: string;
  /** How many increments between particle milestones. Default every 1000. */
  milestone?: number;
  /** Show a floating "+N" above the number on completion. Default true. */
  showFloater?: boolean;
  /** Pulse scale on each tick, default 1.0 = disabled. */
  tickPulseScale?: number;
  /** Callback when complete. */
  onComplete?: () => void;
}

export class NumberRoller {
  private scene: Phaser.Scene;
  private text: Phaser.GameObjects.Text;
  private activeTween?: Phaser.Tweens.Tween;
  private lastTickTime: number = 0;

  constructor(scene: Phaser.Scene, text: Phaser.GameObjects.Text) {
    this.scene = scene;
    this.text = text;
  }

  /**
   * Animate the text from `from` to `to`.
   * If a roll is already in progress, it stops at its current value and
   * starts a new roll from there.
   */
  rollTo(from: number, to: number, opts: NumberRollerOptions = {}): void {
    const {
      duration = 800,
      ease = 'Back.easeOut',
      formatter = (n) => Math.round(n).toLocaleString('en-US'),
      playTicks = true,
      tickSfxKey = 'sfx_score_tick',
      settleSfxKey = 'sfx_combo',
      milestone = 1000,
      showFloater = true,
      tickPulseScale = 1.06,
      onComplete,
    } = opts;

    // Cancel any running tween
    if (this.activeTween) {
      this.activeTween.stop();
      this.activeTween = undefined;
    }

    const delta = to - from;
    if (delta === 0) {
      this.text.setText(formatter(to));
      onComplete?.();
      return;
    }

    const state = { value: from };
    const audio = AudioManager.getInstance();
    let lastMilestoneCrossed = Math.floor(from / milestone);
    let lastDisplayedRounded = Math.round(from);

    this.activeTween = this.scene.tweens.add({
      targets: state,
      value: to,
      duration,
      ease,
      onUpdate: () => {
        const rounded = Math.round(state.value);
        if (rounded === lastDisplayedRounded) return;
        lastDisplayedRounded = rounded;
        this.text.setText(formatter(rounded));

        // Prefer v2 brass tick SFX, fall back to legacy
        const tickKey = this.scene.cache.audio.exists('sfx_number_tick_small')
          ? 'sfx_number_tick_small' : tickSfxKey;

        // Throttled tick SFX — max once every 60ms
        const now = this.scene.time.now;
        if (playTicks && now - this.lastTickTime > 60) {
          audio.playSFX(this.scene, tickKey, { volume: 0.22 });
          this.lastTickTime = now;
        }

        // Pulse scale on the text
        if (tickPulseScale > 1.0 && now - this.lastTickTime < 10) {
          this.scene.tweens.add({
            targets: this.text,
            scaleX: tickPulseScale,
            scaleY: tickPulseScale,
            duration: 40,
            yoyo: true,
            ease: 'Power2',
          });
        }

        // Milestone burst
        const currentMilestone = Math.floor(rounded / milestone);
        if (currentMilestone > lastMilestoneCrossed) {
          lastMilestoneCrossed = currentMilestone;
          this.emitGoldBurst();
        }
      },
      onComplete: () => {
        this.text.setText(formatter(to));
        // Final settle pulse
        this.scene.tweens.add({
          targets: this.text,
          scaleX: 1.15,
          scaleY: 1.15,
          duration: MOTION.dur.milestone,
          yoyo: true,
          ease: 'Back.easeOut',
        });
        // Prefer v2 deep thunk SFX, fall back to legacy
        const settleKey = this.scene.cache.audio.exists('sfx_number_thunk_big')
          ? 'sfx_number_thunk_big' : settleSfxKey;
        audio.playSFX(this.scene, settleKey, { volume: 0.6 });

        if (showFloater && delta > 0) {
          this.spawnFloater(delta, formatter);
        }

        onComplete?.();
      },
    });
  }

  /** Set value instantly with no animation. */
  set(value: number, formatter?: (n: number) => string): void {
    if (this.activeTween) {
      this.activeTween.stop();
      this.activeTween = undefined;
    }
    const fmt = formatter || ((n) => Math.round(n).toLocaleString('en-US'));
    this.text.setText(fmt(value));
  }

  private emitGoldBurst(): void {
    // Small radial burst of gold particles around the text
    const { x, y } = this.text;

    for (let i = 0; i < 6; i++) {
      const angle = (Math.PI * 2 * i) / 6 + Math.random() * 0.3;
      const dist = 40 + Math.random() * 20;
      const particle = this.scene.add.circle(
        x,
        y,
        4 + Math.random() * 3,
        0xfff4c2
      );
      particle.setDepth((this.text.depth || 0) + 1);
      particle.setAlpha(0.9);

      this.scene.tweens.add({
        targets: particle,
        x: x + Math.cos(angle) * dist,
        y: y + Math.sin(angle) * dist,
        alpha: 0,
        scale: 0.3,
        duration: 500,
        ease: 'Power2.easeOut',
        onComplete: () => particle.destroy(),
      });
    }
  }

  private spawnFloater(delta: number, formatter: (n: number) => string): void {
    const { x, y } = this.text;
    const floater = this.scene.add.text(x, y - 40, `+${formatter(delta)}`, {
      fontFamily: FONTS.display,
      fontSize: '44px',
      color: COLORS_HEX.goldLight,
      stroke: COLORS_HEX.abyssBlue,
      strokeThickness: 4,
      shadow: { offsetX: 0, offsetY: 3, color: COLORS_HEX.midnightDepth, blur: 6, fill: true },
    });
    floater.setOrigin(0.5);
    floater.setDepth((this.text.depth || 0) + 2);

    this.scene.tweens.add({
      targets: floater,
      y: y - 120,
      alpha: 0,
      scale: { from: 1.2, to: 0.8 },
      duration: 900,
      ease: 'Power2.easeOut',
      onComplete: () => floater.destroy(),
    });
  }

  destroy(): void {
    if (this.activeTween) {
      this.activeTween.stop();
      this.activeTween = undefined;
    }
  }
}
